import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import { ConfirmationDialog, PageHeader, useToast } from 'components/admin';
import { Button, Input, Select, Table } from 'components/common';
import { getAdminLeaders } from 'apis/adminLeaders';
import { deleteLeaderInformationItem, getAdminLeaderInformationItems } from 'apis/adminLeaderInformationItems';
import { useActiveLanguagesQuery, useAdminDeleteMutation, useAdminListQuery } from 'hooks';
import '../../../styles/admin-shared.scss';

const leaderName = (leader) => leader?.name || leader?.translations?.[0]?.name || `Lãnh đạo ${leader?.id}`;

const LeaderInformationItemsList = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const [page, setPage] = useState(1);
    const [keyword, setKeyword] = useState('');
    const [languageCode, setLanguageCode] = useState('vi');
    const [leaderId, setLeaderId] = useState('');
    const [status, setStatus] = useState('');
    const [deleteId, setDeleteId] = useState(null);
    const params = useMemo(() => ({ pageIndex: page, pageSize: 10, keyword: keyword || undefined, languageCode, leaderId: leaderId || undefined, isActive: status === '' ? undefined : status === 'true' }), [page, keyword, languageCode, leaderId, status]);
    const { data = { items: [], total: 0, currentPage: 1, pageSize: 10 }, isFetching, error } = useAdminListQuery('leader-information-items', params, getAdminLeaderInformationItems);
    const { data: languages = [] } = useActiveLanguagesQuery();
    const { data: leaderData = { items: [] } } = useAdminListQuery('leaders-information-options', { pageSize: 1000, languageCode: languageCode || 'vi' }, getAdminLeaders);
    const leaders = useMemo(() => {
        const uniqueById = new Map();
        (leaderData.items || []).forEach((leader) => {
            const key = Number(leader.id);
            if (Number.isFinite(key) && !uniqueById.has(key)) uniqueById.set(key, leader);
        });
        return Array.from(uniqueById.values());
    }, [leaderData.items]);
    const deleteMutation = useAdminDeleteMutation('leader-information-items', deleteLeaderInformationItem);

    useEffect(() => setPage(1), [keyword, languageCode, leaderId, status]);
    useEffect(() => { if (error) toast.error(error.message || 'Tải dữ liệu thất bại'); }, [error, toast]);

    const remove = async () => {
        try {
            const response = await deleteMutation.mutateAsync(deleteId);
            response.success ? toast.success('Khóa phần thông tin thành công') : toast.error(response.message || 'Khóa thất bại');
        } catch { toast.error('Khóa phần thông tin thất bại'); }
        setDeleteId(null);
    };
    const resolveLeader = (row) => row.leaderName || leaderName(leaders.find((leader) => Number(leader.id) === Number(row.leaderId)));
    const columns = [
        { field: 'id', header: 'ID' },
        { header: 'Lãnh đạo', render: resolveLeader },
        { header: 'Tiêu đề', render: (row) => row.title || row.translation?.[0]?.title || row.translations?.[0]?.title || '—' },
        { field: 'sortOrder', header: 'Thứ tự' },
        { header: 'Trạng thái', render: (row) => <span className={`status-badge ${row.isActive ? 'active' : 'inactive'}`}>{row.isActive ? 'Hoạt động' : 'Không hoạt động'}</span> },
        { header: 'Thao tác', render: (row) => <div className="action-buttons"><button className="icon-btn icon-btn--edit" onClick={() => navigate(`/admin/leader-information-items/edit/${row.id}`)} title="Sửa"><FiEdit2 size={15} /></button><button className="icon-btn icon-btn--delete" onClick={() => setDeleteId(row.id)} title="Khóa"><FiTrash2 size={15} /></button></div> },
    ];

    return <div className="admin-page">
        <PageHeader title="Thông tin lãnh đạo" subtitle="Quản lý các phần thông tin được liên kết với từng lãnh đạo" action={<Button onClick={() => navigate('/admin/leader-information-items/create')}>+ Thêm thông tin</Button>} />
        <div className="filters-bar">
            <div className="filter-item"><Input placeholder="Tìm theo tiêu đề..." value={keyword} onChange={(event) => setKeyword(event.target.value)} /></div>
            <div className="filter-item"><Select value={leaderId} onChange={(event) => setLeaderId(event.target.value)} options={[{ value: '', label: 'Tất cả lãnh đạo' }, ...leaders.map((leader) => ({ value: leader.id, label: leaderName(leader) }))]} /></div>
            <div className="filter-item"><Select value={languageCode} onChange={(event) => setLanguageCode(event.target.value)} options={languages.map((language) => ({ value: language.code, label: language.name }))} /></div>
            <div className="filter-item"><Select value={status} onChange={(event) => setStatus(event.target.value)} options={[{ value: '', label: 'Tất cả trạng thái' }, { value: 'true', label: 'Hoạt động' }, { value: 'false', label: 'Không hoạt động' }]} /></div>
        </div>
        <Table columns={columns} data={data.items || []} loading={isFetching} pagination={{ currentPage: data.currentPage, totalPages: Math.ceil(data.total / data.pageSize) || 1, onPageChange: setPage }} />
        <ConfirmationDialog open={Boolean(deleteId)} onClose={() => setDeleteId(null)} onConfirm={remove} title="Khóa phần thông tin" message="Bạn có chắc chắn muốn khóa phần thông tin này không?" />
    </div>;
};

export default LeaderInformationItemsList;
